import {
  Box,
  Button,
  Code,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { QRCodeSVG } from "qrcode.react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { OpenAPI } from "@/client"
import { VisionButton, VisionInput } from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

type Status = {
  enabled: boolean
  confirmed_at: string | null
  recovery_codes_remaining: number
  required: boolean
}
type SetupResponse = { secret: string; otpauth_uri: string }

function apiFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

async function readJson(r: Response) {
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error((data?.detail as string) || "Request failed")
  return data
}

const TwoStepVerification = () => {
  const { t } = useTranslation(["settings", "common"])
  const sc = useSemanticColors()
  const { showToast, showSuccessToast, showErrorToast } = useCustomToast()
  const queryClient = useQueryClient()

  // Local enrollment flow state.
  const [setup, setSetup] = useState<SetupResponse | null>(null)
  const [code, setCode] = useState("")
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [disableCode, setDisableCode] = useState("")

  const statusQuery = useQuery({
    queryKey: ["2sv-status"],
    queryFn: () =>
      apiFetch("/api/v1/2sv/status").then(readJson) as Promise<Status>,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["2sv-status"] })
    queryClient.invalidateQueries({ queryKey: ["currentUser"] })
  }

  const beginSetup = useMutation({
    mutationFn: () =>
      apiFetch("/api/v1/2sv/setup", { method: "POST" }).then(
        readJson,
      ) as Promise<SetupResponse>,
    onSuccess: (data) => setSetup(data),
    onError: (e: Error) => showErrorToast(e.message),
  })

  const enable = useMutation({
    mutationFn: () =>
      apiFetch("/api/v1/2sv/enable", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      }).then(readJson) as Promise<{ recovery_codes: string[] }>,
    onSuccess: (data) => {
      setRecoveryCodes(data.recovery_codes)
      setSetup(null)
      setCode("")
      refresh()
      showSuccessToast(
        t("settings:twoSv.enabledToast", "Two-step verification enabled"),
      )
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  const disable = useMutation({
    mutationFn: () =>
      apiFetch("/api/v1/2sv/disable", {
        method: "POST",
        body: JSON.stringify({ code: disableCode.trim() || null }),
      }).then(readJson),
    onSuccess: () => {
      setDisableCode("")
      refresh()
      showToast(
        t("settings:twoSv.disabledToast", "Two-step verification disabled"),
        "",
        "success",
      )
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  const status = statusQuery.data

  return (
    <Box>
      <Heading size="md" color={sc.heading} mb={2}>
        {t("settings:twoSv.title", "Two-step verification")}
      </Heading>
      <Text color={sc.textMuted} fontSize="sm" mb={4}>
        {t(
          "settings:twoSv.subtitle",
          "Add a one-time code from an authenticator app to your login.",
        )}
      </Text>

      {statusQuery.isLoading ? (
        <Flex justify="center" py={6}>
          <Spinner size="md" color={sc.spinnerColor} />
        </Flex>
      ) : recoveryCodes ? (
        // One-time display of recovery codes right after enabling.
        <Box>
          <Text color={sc.heading} fontWeight="600" mb={2}>
            {t("settings:twoSv.saveCodes", "Save your recovery codes")}
          </Text>
          <Text color={sc.textMuted} fontSize="sm" mb={3}>
            {t(
              "settings:twoSv.saveCodesHelp",
              "Each code works once if you lose your authenticator. They won't be shown again.",
            )}
          </Text>
          <SimpleGrid columns={2} gap={2} mb={4}>
            {recoveryCodes.map((c) => (
              <Code key={c} px={2} py={1} fontSize="sm">
                {c}
              </Code>
            ))}
          </SimpleGrid>
          <VisionButton
            variant="brand"
            size="sm"
            onClick={() => setRecoveryCodes(null)}
          >
            {t("settings:twoSv.savedCodes", "I've saved them")}
          </VisionButton>
        </Box>
      ) : status?.enabled ? (
        // Enrolled: show state + disable.
        <Box>
          <Text color={sc.heading} mb={1}>
            ✅ {t("settings:twoSv.on", "Two-step verification is on.")}
          </Text>
          <Text color={sc.textMuted} fontSize="sm" mb={4}>
            {t("settings:twoSv.recoveryRemaining", {
              defaultValue: "{{count}} recovery codes remaining.",
              count: status.recovery_codes_remaining,
            })}
          </Text>
          <Field
            label={t("settings:twoSv.disableLabel", "Enter a code to disable")}
          >
            <VisionInput
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              placeholder="123456"
              variant="glass"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </Field>
          <Box mt={3}>
            <Button
              colorScheme="red"
              variant="outline"
              size="sm"
              disabled={disable.isPending}
              onClick={() => disable.mutate()}
            >
              {t("settings:twoSv.disable", "Disable two-step verification")}
            </Button>
          </Box>
        </Box>
      ) : setup ? (
        // Mid-enrollment: QR + confirm code.
        <Box>
          <Text color={sc.textMuted} fontSize="sm" mb={3}>
            {t(
              "settings:twoSv.scan",
              "Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.",
            )}
          </Text>
          <Box bg="white" p={3} borderRadius="md" w="fit-content" mb={3}>
            <QRCodeSVG value={setup.otpauth_uri} size={168} />
          </Box>
          <Text color={sc.textMuted} fontSize="xs" mb={3}>
            {t("settings:twoSv.manualKey", "Or enter this key manually:")}{" "}
            <Code fontSize="xs">{setup.secret}</Code>
          </Text>
          <Field label={t("settings:twoSv.codeLabel", "Verification code")}>
            <VisionInput
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              variant="glass"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </Field>
          <Flex gap={2} mt={3}>
            <VisionButton
              variant="brand"
              size="sm"
              loading={enable.isPending}
              onClick={() => enable.mutate()}
            >
              {t("settings:twoSv.confirm", "Confirm and enable")}
            </VisionButton>
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setSetup(null)
                setCode("")
              }}
            >
              {t("common:actions.cancel", "Cancel")}
            </VisionButton>
          </Flex>
        </Box>
      ) : (
        // Not enrolled: start (with a nudge if the policy requires it).
        <Box>
          {status?.required && (
            <Box
              mb={3}
              p={3}
              borderRadius="md"
              bg="orange.50"
              _dark={{ bg: "orange.900" }}
              borderWidth="1px"
              borderColor="orange.300"
            >
              <Text fontSize="sm" color={sc.heading}>
                {t(
                  "settings:twoSv.requiredNudge",
                  "Your administrator requires two-step verification. Please set it up now.",
                )}
              </Text>
            </Box>
          )}
          <VisionButton
            variant="brand"
            size="sm"
            loading={beginSetup.isPending}
            onClick={() => beginSetup.mutate()}
          >
            {t("settings:twoSv.setup", "Set up two-step verification")}
          </VisionButton>
        </Box>
      )}
    </Box>
  )
}

export default TwoStepVerification
